'use client';

import { Chip, Tooltip } from '@mui/material';
import clsx from 'clsx';
import { LuListFilter, LuX } from 'react-icons/lu';
import { CountBadge } from '../CountBadge/CountBadge';
import { ROW_ACTION_BUTTON, ROW_ACTION_SIZE } from '../RowAction/row-action';

// Toolbar building blocks shared by the grid toolbars (applications, positions, add candidates, job activity): segmented
// groups of flush 40px controls, the Filter button with its count, and the brand filter chips.

/** One bordered, shadowed box per group; its controls sit flush inside, split by hairlines. */
export const TOOLBAR_GROUP = 'flex h-10 shrink-0 items-center overflow-hidden rounded-md border border-border bg-page shadow-(--btn-shadow)';
export const TOOLBAR_SEP = 'h-full w-px shrink-0 bg-border';
export const TOOLBAR_SEGMENT = 'inline-flex h-full shrink-0 items-center gap-2 px-3 text-sm font-medium whitespace-nowrap text-foreground-muted transition-colors duration-150 ease-out hover:bg-foreground/5 hover:text-foreground active:bg-foreground/10 disabled:pointer-events-none disabled:opacity-45 [&>svg]:size-4 [&>svg]:shrink-0';
export const TOOLBAR_SEGMENT_ICON = clsx(TOOLBAR_SEGMENT, 'w-10 justify-center px-0');
/** Space between a toolbar's sections (search, groups, chips): Tailwind `gap-3`. */
export const TOOLBAR_SECTION_GAP = 12;

/** One control in a segmented group: the hairline before it, then the button (icon, plus its word when `label`). */
export function ToolbarSegment({
  ariaLabel,
  tooltip,
  icon: Icon,
  label,
  disabled,
  destructive,
  first,
  onClick,
  children,
  buttonProps
}) {
  return <span className="flex h-full shrink-0 items-center">
            {!first && <span className={TOOLBAR_SEP} aria-hidden />}
            <Tooltip arrow title={tooltip} describeChild>
                {/* The span keeps the tooltip working while the button is disabled; h-full so the hover fills the group. */}
                <span className="inline-flex h-full">
                    <button type="button" aria-label={ariaLabel} disabled={disabled} onClick={onClick} {...buttonProps} className={clsx(label || children ? TOOLBAR_SEGMENT : TOOLBAR_SEGMENT_ICON, destructive && 'hover:bg-error/10! hover:text-error!')}>
                        {Icon && <Icon />}
                        {label}
                        {children}
                    </button>
                </span>
            </Tooltip>
        </span>;
}

/**
 * The Filter segment: icon, the word (folding away when the toolbar `@container` is under 900px) and a `CountBadge` of
 * the applied filters. Put it in a `TOOLBAR_GROUP`, alone or first.
 */
export function ToolbarFilterSegment({
  count,
  onClick,
  first = true,
  buttonProps
}) {
  return <ToolbarSegment first={first} ariaLabel={count ? `Filter, ${count} applied` : 'Filter'} tooltip={count ? `${count} filter${count === 1 ? '' : 's'} applied` : 'Filter'} onClick={onClick} buttonProps={buttonProps}>
            <LuListFilter />
            <span className="@max-[900px]:hidden">Filter</span>
            {count > 0 && <CountBadge count={count} />}
        </ToolbarSegment>;
}

/** Active filter chips (and their "+N"): brand tint, brand border, the toolbar's 40px height. */
export const FILTER_CHIP_STYLE = {
  backgroundColor: 'color-mix(in srgb, var(--color-brand) 14%, var(--page))',
  color: 'var(--color-brand)',
  height: '40px',
  padding: '0 4px',
  borderRadius: '6px',
  border: '1px solid color-mix(in srgb, var(--color-brand) 45%, var(--border))'
};

/** One active filter: its label, the full condition in the tooltip, and × to remove it. */
export function FilterChip({
  label,
  tooltip,
  onDelete,
  'data-testid': testId
}) {
  return <Tooltip title={tooltip} arrow>
            <Chip variant="filled" label={label}
    // Label 6px in from a 4px edge (~10px to the border, like the segment buttons); the ×'s square sits on the chip's
    // 4px edge, pulled 4px toward the label so its built-in inset doesn't leave a gap.
    className="max-w-56 min-w-0 shrink [&_.MuiChip-deleteIcon]:mr-0! [&_.MuiChip-deleteIcon]:-ml-1! [&_.MuiChip-label]:px-1.5!" style={FILTER_CHIP_STYLE}
    // The × as a row action: a 24px square that tints on hover. Chip clones this element with its class and
    // click handler; the icon keeps the chip's brand colour instead of the row action's muted grey.
    deleteIcon={<span className={clsx(ROW_ACTION_BUTTON, ROW_ACTION_SIZE.table, 'cursor-pointer text-brand/75!')}>
                        <LuX aria-hidden />
                    </span>} onDelete={onDelete} data-testid={testId} />
        </Tooltip>;
}

/** Operators that filter without a value. */
const VALUELESS_OPERATORS = new Set(['isEmpty', 'isNotEmpty']);

/** A filter item that actually narrows the list (one still being filled in the panel doesn't): what chips and counts show. */
export const isFilterApplied = item => VALUELESS_OPERATORS.has(item.operator) || item.value != null && item.value !== '' && !(Array.isArray(item.value) && item.value.length === 0);